import { FC, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, Search } from 'lucide-react';
import { trpc, CoverageArea } from 'src/trpc';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from 'src/components/useToast';
import { StatusDisplay } from 'src/components/StatusDisplay';

interface OrganizationUnassignedAreasTabProps {
  organizationId: string;
  onDataChange: (hasChanges: boolean) => void;
}

export const OrganizationUnassignedAreasTab: FC<OrganizationUnassignedAreasTabProps> = ({
  organizationId,
  onDataChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAreas, setSelectedAreas] = useState<Set<string>>(new Set());
  const { successToast, errorToast } = useToast();

  const utils = trpc.useUtils();

  // Fetch unassigned coverage areas
  const { data: unassignedAreas = [], isLoading } =
    trpc.admin.organization.getUnassignedCoverageAreas.useQuery();

  const assignCoverageAreasMutation =
    trpc.admin.organization.assignCoverageAreasToOrganization.useMutation({
      onSuccess: (result) => {
        // Clear selections and refetch data
        setSelectedAreas(new Set());
        utils.admin.organization.getOrganizationCoverageAreas.invalidate(organizationId || '');
        utils.admin.organization.getUnassignedCoverageAreas.invalidate();

        successToast({
          title: 'Coverage Areas Assigned',
          description: `Successfully assigned ${result.modifiedCount} coverage area${
            result.modifiedCount === 1 ? '' : 's'
          }. They are now active and assigned to your organization.`,
        });
        onDataChange(true);
      },
      onError: (error) => {
        errorToast({
          title: 'Failed to Assign Coverage Areas',
          description: error.message || 'An error occurred while assigning the coverage areas.',
        });
      },
    });

  // Filter areas based on search term
  const filteredAreas = unassignedAreas.filter(
    (area: CoverageArea) =>
      area.zipCode.includes(searchTerm) ||
      (area.city && area.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (area.state && area.state.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  const handleSelectAll = () => {
    if (selectedAreas.size === filteredAreas.length) {
      // If all are selected, clear selection
      setSelectedAreas(new Set());
    } else {
      // Otherwise, select all filtered areas
      const newSelection = new Set<string>();
      filteredAreas.forEach((area: CoverageArea) => newSelection.add(area._id));
      setSelectedAreas(newSelection);
    }
  };

  const handleToggleSelection = (areaId: string) => {
    const newSelection = new Set(selectedAreas);
    if (newSelection.has(areaId)) {
      newSelection.delete(areaId);
    } else {
      newSelection.add(areaId);
    }
    setSelectedAreas(newSelection);
  };

  const handleAssignSelected = () => {
    if (selectedAreas.size === 0) return;

    assignCoverageAreasMutation.mutate({
      organizationId,
      coverageAreaIds: Array.from(selectedAreas),
    });
  };

  const hasUnassignedAreas = filteredAreas.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          {hasUnassignedAreas && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleSelectAll}>
                {selectedAreas.size === filteredAreas.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleAssignSelected}
                disabled={selectedAreas.size === 0 || assignCoverageAreasMutation.isPending}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {assignCoverageAreasMutation.isPending
                  ? `Assigning ${selectedAreas.size} area${selectedAreas.size === 1 ? '' : 's'}...`
                  : `Assign ${selectedAreas.size} area${selectedAreas.size === 1 ? '' : 's'}`}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Search Row */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search zip code, city, or state..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow border">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading unassigned coverage areas...</p>
            </div>
          </div>
        ) : !hasUnassignedAreas ? (
          <div className="p-8 text-center">
            <CheckCircle className="w-12 h-12 mx-auto text-green-300 mb-3" />
            <h3 className="text-lg font-medium text-gray-500">No unassigned coverage areas</h3>
            <p className="text-gray-400">All coverage areas have been assigned to organizations.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <Checkbox
                    checked={selectedAreas.size > 0 && selectedAreas.size === filteredAreas.length}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Zip Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  City
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  State
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAreas.map((area: CoverageArea) => (
                <tr key={area._id} className={selectedAreas.has(area._id) ? 'bg-blue-50' : ''}>
                  <td className="px-4 py-4">
                    <Checkbox
                      checked={selectedAreas.has(area._id)}
                      onCheckedChange={() => handleToggleSelection(area._id)}
                      aria-label={`Select ${area.zipCode}`}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{area.zipCode}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{area.city || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{area.state || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusDisplay status={area.active ? 'active' : 'suspended'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
